import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, PointerSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export default function ModuleEditor({
  mod,
  idx,
  setModules,
  categories,
  blogCategories,
  availableBrands,
  globalBrandLogos,
  setGlobalBrandLogos,
  handleFileUpload,
  preserveEditorScroll,
  resolveUploadUrl,
  TextEditor,
  LocalInput,
  LocalNumberInput,
  LocalSelect,
  LocalSelectMultiple,
  // SlideEditor, // We will use the local definition to avoid prop drilling issues
  getToken,
  deleteBrandLogo,
  setBrandLogoUrl,
  uploadBrandLogo,
  toast,
  onPickMedia,
}) {
  const flattenBlogCategoryNames = (nodes) => {
    const out = [];
    const walk = (arr) => {
      (arr || []).forEach((n) => {
        if (n && n.name) out.push(n.name);
        if (Array.isArray(n && n.subcategories) && n.subcategories.length) walk(n.subcategories);
      });
    };
    walk(nodes);
    return out;
  };

  const flattenCategoryNames = (nodes) => {
    const out = [];
    const walk = (arr, depth = 0) => {
      (arr || []).forEach((n) => {
        if (n && n.name) {
          const prefix = depth ? `${'—'.repeat(depth)} ` : '';
          out.push({ value: n.name, label: `${prefix}${n.name}` });
        }
        if (Array.isArray(n && n.subcategories) && n.subcategories.length) walk(n.subcategories, depth + 1);
      });
    };
    walk(nodes);
    return out;
  };

  // Internal DnD sensors for slider reordering
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(MouseSensor),
    useSensor(TouchSensor)
  );

  const [activeEditorTab, setActiveEditorTab] = useState(0);

  // const [activeSlideId, setActiveSlideId] = useState(null); // Unused

  const handleDragEndSlides = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setModules(ms => ms.map((m, i) => {
        if (i !== idx) return m;
        const slides = m.config || [];
        const oldIndex = slides.findIndex((_, si) => `slide-${si}` === active.id);
        const newIndex = slides.findIndex((_, si) => `slide-${si}` === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          return { ...m, config: arrayMove(slides, oldIndex, newIndex) };
        }
        return m;
      }));
    }
    // setActiveSlideId(null);
  };

  const ColorPickerField = React.useMemo(() => {
    return function ColorPickerFieldInner({ label, value, defaultColor = '#000000', onCommit }) {
      const [localValue, setLocalValue] = React.useState(value || '');
      React.useEffect(() => {
        setLocalValue(value || '');
      }, [value]);

      const handleCommit = (val) => {
        if (preserveEditorScroll) {
          preserveEditorScroll(() => onCommit(val));
        } else {
          onCommit(val);
        }
      };

      return (
        <div className="admin-form-group" style={{ marginBottom: 12 }}>
          {label && <label className="admin-label">{label}</label>}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <LocalInput
                placeholder={defaultColor}
                value={localValue || ''}
                onCommit={(v) => {
                  setLocalValue(v);
                  handleCommit(v);
                }}
                onChange={(e) => setLocalValue(e.target.value)}
              />
            </div>
            <input
              type="color"
              value={(/^#([0-9a-fA-F]{6})$/.test(String(localValue || '').trim()) ? String(localValue).trim() : defaultColor)}
              onChange={(e) => {
                setLocalValue(e.target.value);
              }}
              onBlur={(e) => {
                handleCommit(e.target.value);
              }}
              onFocus={() => { if (preserveEditorScroll) preserveEditorScroll(); }}
              onClick={(e) => { e.stopPropagation(); if (preserveEditorScroll) preserveEditorScroll(); }}
              onPointerDown={(e) => { e.stopPropagation(); if (preserveEditorScroll) preserveEditorScroll(); }}
              onMouseDown={(e) => { e.stopPropagation(); if (preserveEditorScroll) preserveEditorScroll(); }}
              onTouchStart={(e) => { e.stopPropagation(); if (preserveEditorScroll) preserveEditorScroll(); }}
              title="Pick a color"
              style={{ width: 34, height: 34, padding: 0, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', flexShrink: 0 }}
            />
          </div>
        </div>
      );
    }
  }, [preserveEditorScroll]);

  const InlineColorPicker = React.useMemo(() => {
    return function InlineColorPickerInner({ value, defaultColor = '#000000', onCommit }) {
      const [localValue, setLocalValue] = React.useState(value || '');
      React.useEffect(() => {
        setLocalValue(value || '');
      }, [value]);

      const handleCommit = (val) => {
        if (preserveEditorScroll) {
          preserveEditorScroll(() => onCommit(val));
        } else {
          onCommit(val);
        }
      };

      return (
        <input
          type="color"
          value={(/^#([0-9a-fA-F]{6})$/.test(String(localValue || '').trim()) ? String(localValue).trim() : defaultColor)}
          onChange={(e) => {
            setLocalValue(e.target.value);
          }}
          onBlur={(e) => {
            handleCommit(e.target.value);
          }}
          onFocus={() => { if (preserveEditorScroll) preserveEditorScroll(); }}
          onClick={(e) => { e.stopPropagation(); if (preserveEditorScroll) preserveEditorScroll(); }}
          onPointerDown={(e) => { e.stopPropagation(); if (preserveEditorScroll) preserveEditorScroll(); }}
          onMouseDown={(e) => { e.stopPropagation(); if (preserveEditorScroll) preserveEditorScroll(); }}
          onTouchStart={(e) => { e.stopPropagation(); if (preserveEditorScroll) preserveEditorScroll(); }}
          title="Pick a color"
          style={{ width: 28, height: 28, padding: 0, border: '1px solid #e2e8f0', borderRadius: 14, background: '#fff', cursor: 'pointer', flexShrink: 0, overflow: 'hidden' }}
        />
      );
    }
  }, [preserveEditorScroll]);

  // Small helper: "Media" button that opens the library picker
  const MediaBtn = ({ onPick }) => (
    <button
      type="button"
      onClick={() => onPickMedia && onPickMedia(onPick)}
      title="Pick from Media Library"
      style={{
        flexShrink: 0,
        padding: '0 10px',
        height: 32,
        borderRadius: 6,
        border: '1px solid #cbd5e1',
        background: '#f8fafc',
        cursor: onPickMedia ? 'pointer' : 'not-allowed',
        fontSize: 12,
        fontWeight: 600,
        color: '#334155',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        opacity: onPickMedia ? 1 : 0.5,
      }}
    >
      <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
      Media
    </button>
  );

  return (
    <div className="module-editor-wrap">
      {/* Module Content Types */}
      {mod.type === 'text' && (
        <TextEditor
          value={mod.config.text || ''}
          onCommit={(val) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, text: val } } : m))}
        />
      )}

      {mod.type === 'html' && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>HTML &amp; JavaScript Content</label>
            <span style={{ fontSize: 11, color: '#0ea5e9', background: '#f0f9ff', border: '1px solid #bae6fd', padding: '2px 8px', borderRadius: 12, fontWeight: 500 }}>
              Supports &lt;script&gt;, &lt;style&gt;, HTML &amp; JS
            </span>
          </div>
          <textarea
            value={mod.config.html || ''}
            onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, html: e.target.value } } : m))}
            rows={10}
            placeholder="<!-- HTML markup, <style> tags, or JavaScript (<script> or raw JS code) -->&#10;<div id='custom-promo'>Hello from custom module!</div>&#10;&#10;<script>&#10;  console.log('Custom JavaScript executed successfully!');&#10;</script>"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 13, lineHeight: 1.5 }}
          />
          <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>
            HTML tags, styles (<code>&lt;style&gt;</code>), and JavaScript (<code>&lt;script&gt;</code> tags or raw JS) will be rendered and executed when this page is displayed.
          </div>
        </div>
      )}

      {mod.type === 'head-tags' && (
        <div style={{ display: 'grid', gap: 8 }}>
          <label style={{ fontSize: 12, color: '#64748b' }}>Head Tags (Scripts, Meta, etc.)</label>
          <textarea
            value={mod.config.code || ''}
            onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, code: e.target.value } } : m))}
            rows={8}
            placeholder="<!-- Google tag (gtag.js) -->&#10;<script async src=...></script>"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 13 }}
          />
          <div style={{ fontSize: 11, color: '#eab308' }}><strong>Caution:</strong> These tags will be injected directly into the &lt;head&gt; of this specific page.</div>
        </div>
      )}

      {mod.type === 'headline' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <LocalInput
            label="Headline"
            placeholder="Headline text..."
            value={mod.config.text || ''}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, text: v } } : m))}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
            <div className="admin-form-group">
              <label className="admin-label">Color (optional)</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <LocalInput
                    placeholder="#111827"
                    value={mod.config.color || ''}
                    onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, color: v } } : m))}
                  />
                </div>

                <input
                  type="color"
                  value={(/^#([0-9a-fA-F]{6})$/.test(String(mod.config.color || '').trim()) ? String(mod.config.color).trim() : '#111827')}
                  onChange={(e) => {
                    const v = e.target.value;
                    preserveEditorScroll(() => {
                      setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, color: v } } : m));
                    });
                  }}
                  onFocus={() => preserveEditorScroll()}
                  onClick={(e) => { e.stopPropagation(); preserveEditorScroll(); }}
                  onPointerDown={(e) => { e.stopPropagation(); preserveEditorScroll(); }}
                  onMouseDown={(e) => { e.stopPropagation(); preserveEditorScroll(); }}
                  onTouchStart={(e) => { e.stopPropagation(); preserveEditorScroll(); }}
                  title="Pick a color"
                  style={{ width: 34, height: 34, padding: 0, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }}
                />

                {['#111827', '#f97316'].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => preserveEditorScroll(() => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, color: c } } : m)))}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    title={c}
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      border: '1px solid #e2e8f0',
                      background: c,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  />
                ))}
              </div>
            </div>
            <LocalNumberInput
              label="Font Size (px, optional)"
              value={Number(mod.config.fontSize || 0)}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, fontSize: Number(v || 0) } } : m))}
            />
          </div>
          <LocalSelect
            label="Alignment"
            value={mod.config.align || 'left'}
            options={[
              { value: 'left', label: 'Left' },
              { value: 'center', label: 'Center' },
              { value: 'right', label: 'Right' },
            ]}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, align: v } } : m))}
            style={{ maxWidth: 220 }}
          />
        </div>
      )}

      {mod.type === 'image' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <LocalInput label="Image URL" placeholder="https://..." value={mod.config.url || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, url: v } } : m))} />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
              Upload Image
              <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.url', e.target.files[0]); }} />
            </label>
            <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, url } } : m))} />
            {mod.config.url ? <img src={resolveUploadUrl(mod.config.url)} alt="preview" style={{ height: 60, width: 120, objectFit: 'contain', background: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0' }} /> : null}
          </div>
          <LocalInput label="Alt Text" placeholder="Image description" value={mod.config.alt || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, alt: v } } : m))} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <LocalNumberInput
              label="Width (px)"
              value={Number(mod.config.width || 0)}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, width: Number(v || 0) } } : m))}
            />
            <LocalNumberInput
              label="Height (px)"
              value={Number(mod.config.height || 0)}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, height: Number(v || 0) } } : m))}
            />
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            Leave 0 to auto-size. If you set a height, the image will fit without being cropped.
          </div>
        </div>
      )}

      {mod.type === 'button' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <LocalInput
            label="Button Text"
            placeholder="e.g. Shop Now"
            value={mod.config.text || ''}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, text: v } } : m))}
          />
          <LocalInput
            label="Link URL"
            placeholder="/products or https://..."
            value={mod.config.link || ''}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, link: v } } : m))}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <LocalSelect
              label="Alignment"
              value={mod.config.align || 'left'}
              options={[
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
              ]}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, align: v } } : m))}
            />
            <LocalSelect
              label="Variant"
              value={mod.config.variant || 'primary'}
              options={[
                { value: 'primary', label: 'Primary' },
                { value: 'secondary', label: 'Secondary' },
                { value: 'outline', label: 'Outline' },
              ]}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, variant: v } } : m))}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={!!mod.config.newTab}
              onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, newTab: e.target.checked } } : m))}
            />
            Open in new tab
          </label>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, marginTop: 12 }}>
            Button Colors (Overrides Variant):
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <ColorPickerField label="Button BG" value={mod.config.buttonBgColor} defaultColor="#2563eb" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonBgColor: v } } : m))} />
            <ColorPickerField label="Button Text" value={mod.config.buttonTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonTextColor: v } } : m))} />
            <ColorPickerField label="Hover BG" value={mod.config.buttonHoverBgColor} defaultColor="#1d4ed8" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverBgColor: v } } : m))} />
            <ColorPickerField label="Hover Text" value={mod.config.buttonHoverTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverTextColor: v } } : m))} />
          </div>
        </div>
      )}

      {mod.type === 'divider' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <LocalNumberInput
              label="Thickness (px)"
              value={Number(mod.config.thickness || 1)}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, thickness: Number(v || 0) } } : m))}
            />
            <LocalNumberInput
              label="Width (%)"
              value={Number(mod.config.width || 100)}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, width: Number(v || 0) } } : m))}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <LocalInput
              label="Color"
              placeholder="#e2e8f0"
              value={mod.config.color || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, color: v } } : m))}
            />
            <LocalSelect
              label="Style"
              value={mod.config.style || 'solid'}
              options={[
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
                { value: 'dotted', label: 'Dotted' },
              ]}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, style: v } } : m))}
            />
          </div>
          <LocalNumberInput
            label="Vertical Margin (px)"
            value={Number(mod.config.margin || 24)}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, margin: Number(v || 0) } } : m))}
            style={{ maxWidth: 220 }}
          />
        </div>
      )}

      {mod.type === 'video' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <LocalInput
            label="Video URL"
            placeholder="YouTube, Vimeo, or MP4"
            value={mod.config.url || ''}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, url: v } } : m))}
          />
          <LocalInput
            label="Title (optional)"
            placeholder="Video title"
            value={mod.config.title || ''}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))}
          />
          <LocalSelect
            label="Aspect Ratio"
            value={mod.config.aspect || '16:9'}
            options={[
              { value: '16:9', label: '16:9' },
              { value: '21:9', label: '21:9' },
              { value: '4:3', label: '4:3' },
              { value: '1:1', label: '1:1' },
            ]}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, aspect: v } } : m))}
            style={{ maxWidth: 220 }}
          />
          <div style={{ display: 'grid', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={!!mod.config.autoplay}
                onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, autoplay: e.target.checked } } : m))}
              />
              Autoplay
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={!!mod.config.muted}
                onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, muted: e.target.checked } } : m))}
              />
              Muted
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={mod.config.controls !== false}
                onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, controls: e.target.checked } } : m))}
              />
              Show controls
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={!!mod.config.loop}
                onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, loop: e.target.checked } } : m))}
              />
              Loop
            </label>
          </div>
        </div>
      )}

      {mod.type === 'vertical-banner' && (
        <div style={{ display: 'grid', gap: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <LocalSelect
              label="Animation Effect"
              value={mod.config.effect || 'fade'}
              options={[
                { value: 'fade', label: 'Fade' },
                { value: 'slide', label: 'Slide' },
              ]}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, effect: v } } : m))}
            />
            <LocalNumberInput
              label="Transition Speed (seconds)"
              value={mod.config.speed || 5}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, speed: Number(v) || 5 } } : m))}
            />
          </div>

          <div style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>BANNER ITEMS</span>
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-xs"
                onClick={() => setModules(ms => ms.map((m, i) => {
                  if (i !== idx) return m;
                  const items = Array.isArray(m.config?.items) ? m.config.items : [];
                  return {
                    ...m,
                    config: {
                      ...(m.config || {}),
                      items: [...items, { image: '', alt: 'Banner', link: '' }]
                    }
                  };
                }))}
              >
                + Add Banner
              </button>
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              {(Array.isArray(mod.config.items) ? mod.config.items : []).map((item, itemIdx) => (
                <SlideEditor
                  key={itemIdx}
                  slide={item}
                  removeLabel="Remove Banner"
                  onCommit={(updated) => setModules(ms => ms.map((m, i) => {
                    if (i !== idx) return m;
                    const items = m.config.items.map((it, ii) => ii === itemIdx ? updated : it);
                    return { ...m, config: { ...m.config, items } };
                  }))}
                  onRemove={() => setModules(ms => ms.map((m, i) => {
                    if (i !== idx) return m;
                    const items = (m.config.items || []).filter((_, ii) => ii !== itemIdx);
                    return { ...m, config: { ...m.config, items } };
                  }))}
                  onUpload={(file) => handleFileUpload(idx, `config.items.${itemIdx}.image`, file)}
                  resolveUploadUrl={resolveUploadUrl}
                  LocalInput={LocalInput}
                  onPickMedia={onPickMedia}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {mod.type === 'spacer' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <LocalNumberInput
            label="Height (px)"
            value={Number(mod.config.height || 0)}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, height: Number(v || 0) } } : m))}
            style={{ maxWidth: 220 }}
          />
          <div style={{ fontSize: 12, color: '#64748b' }}>
            Adds empty vertical space. Set to 0 to hide.
          </div>
        </div>
      )}

      {mod.type === 'image-text-button' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <LocalInput
                label="Title"
                value={mod.config.title || ''}
                onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))}
              />
            </div>
            <div style={{ paddingBottom: 6 }}>
              <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
            </div>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 12, color: '#64748b' }}>Text</div>
            <TextEditor
              value={mod.config.text || ''}
              onCommit={(val) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, text: val } } : m))}
            />
          </div>

          <LocalInput
            label="Image URL"
            value={mod.config.image || ''}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, image: v } } : m))}
          />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
              Upload Image
              <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.image', e.target.files[0]); }} />
            </label>
            <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, image: url } } : m))} />
            {mod.config.image ? <img src={resolveUploadUrl(mod.config.image)} alt="preview" style={{ height: 60, objectFit: 'cover' }} /> : null}
          </div>
          <LocalInput
            label="Image Alt"
            value={mod.config.imageAlt || ''}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageAlt: v } } : m))}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <LocalSelect
              label="Image Position"
              value={mod.config.imagePosition || 'left'}
              options={[
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' }
              ]}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imagePosition: v } } : m))}
            />
            <LocalSelect
              label="Button Align"
              value={mod.config.buttonAlign || 'left'}
              options={[
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
              ]}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonAlign: v } } : m))}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <LocalInput
              label="Button Text"
              value={mod.config.buttonText || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonText: v } } : m))}
            />
            <LocalInput
              label="Button Link"
              value={mod.config.buttonLink || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonLink: v } } : m))}
            />
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, marginTop: 12 }}>
            Button Colors:
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <ColorPickerField label="Button BG" value={mod.config.buttonBgColor} defaultColor="#2563eb" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonBgColor: v } } : m))} />
            <ColorPickerField label="Button Text" value={mod.config.buttonTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonTextColor: v } } : m))} />
            <ColorPickerField label="Hover BG" value={mod.config.buttonHoverBgColor} defaultColor="#1d4ed8" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverBgColor: v } } : m))} />
            <ColorPickerField label="Hover Text" value={mod.config.buttonHoverTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverTextColor: v } } : m))} />
          </div>
        </div>
      )}

      {mod.type === 'slider' && (
        <DndContext sensors={sensors} collisionDetection={closestCenter}
          onDragEnd={handleDragEndSlides}
        >
          <SortableContext items={(mod.config || []).map((_, si) => `slide-${si}`)} strategy={verticalListSortingStrategy}>
            <div style={{ display: 'grid', gap: 12 }}>
              {(mod.config || []).map((s, si) => (
                <SortableSlide key={`slide-${si}`} id={`slide-${si}`}>
                  {({ attributes, listeners, isDragging }) => (
                    <div style={{ position: 'relative', opacity: isDragging ? 0.5 : 1 }}>
                      <div {...attributes} {...listeners} style={{ position: 'absolute', left: 8, top: 20, zIndex: 10, cursor: 'grab', padding: 4 }}>
                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#94a3b8"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" /></svg>
                      </div>
                      <div style={{ paddingLeft: 30 }}>
                        <SlideEditor
                          slide={s}
                          onCommit={(updated) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: m.config.map((it, ii) => ii === si ? updated : it) } : m))}
                          onRemove={() => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: m.config.filter((_, ii) => ii !== si) } : m))}
                          onUpload={(file) => handleFileUpload(idx, `config.${si}.image`, file)}
                          onUploadMobile={(file) => handleFileUpload(idx, `config.${si}.imageMobile`, file)}
                          resolveUploadUrl={resolveUploadUrl}
                          LocalInput={LocalInput}
                          onPickMedia={onPickMedia}
                        />
                      </div>
                    </div>
                  )}
                </SortableSlide>
              ))}
            </div>
          </SortableContext>
          <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" style={{ marginTop: 12 }} onClick={() => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: [...(m.config || []), { image: '', title: '', link: '' }] } : m))}>+ Add Slide</button>
        </DndContext>
      )}

      {mod.type === 'oferta' && (
        <div style={{ display: 'grid', gap: 20 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <LocalInput
                label="Module Title"
                placeholder="e.g. OFERTA SOT"
                value={(mod.config && mod.config.title) || ''}
                onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...(m.config || {}), title: v } } : m))}
              />
            </div>
            <div style={{ paddingBottom: 6 }}>
              <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#003b49" />
            </div>
          </div>

          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, marginTop: -4 }}>
            Cards Button Colors:
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <ColorPickerField label="Button BG" value={mod.config.buttonBgColor} defaultColor="#1e3a8a" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonBgColor: v } } : m))} />
            <ColorPickerField label="Button Text" value={mod.config.buttonTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonTextColor: v } } : m))} />
            <ColorPickerField label="Hover BG" value={mod.config.buttonHoverBgColor} defaultColor="#f97316" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverBgColor: v } } : m))} />
            <ColorPickerField label="Hover Text" value={mod.config.buttonHoverTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverTextColor: v } } : m))} />
          </div>

          <div style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>TABS</span>
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-xs"
                onClick={() => setModules(ms => ms.map((m, i) => {
                  if (i !== idx) return m;
                  const tabs = Array.isArray(m.config?.tabs) ? m.config.tabs : [];
                  return {
                    ...m,
                    config: {
                      ...(m.config || {}),
                      tabs: [...tabs, { label: 'New Tab', cards: [{ image: '', link: '', buttonText: 'BLI TANI' }, { image: '', link: '', buttonText: 'BLI TANI' }, { image: '', link: '', buttonText: 'BLI TANI' }] }]
                    }
                  };
                }))}
              >
                + Add Tab
              </button>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
              {(() => {
                const oTitle = mod.config?.title || '';
                const isOfertaSot = oTitle === 'OFERTA SOT';
                return (
                  <button
                    type="button"
                    onClick={() => setActiveEditorTab(-1)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 800,
                      background: activeEditorTab === -1 ? '#0073be' : '#f8fafc',
                      color: activeEditorTab === -1 ? '#fff' : '#64748b',
                      border: activeEditorTab === -1 ? '1px solid #0073be' : '1px solid #e2e8f0',
                      cursor: isOfertaSot ? 'pointer' : 'default',
                      opacity: isOfertaSot ? 1 : 0.5,
                      textTransform: 'uppercase'
                    }}
                    disabled={!isOfertaSot}
                  >
                    {oTitle || 'No Title'} {isOfertaSot && <span style={{ fontSize: 10, opacity: 0.7 }}>(Slider)</span>}
                  </button>
                );
              })()}

              <div style={{ width: 1, height: 24, background: '#e2e8f0' }} />

              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', flex: 1 }} className="hide-scrollbar">
                {(mod.config?.tabs || []).map((tab, ti) => (
                  <div key={ti} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button
                      type="button"
                      onClick={() => setActiveEditorTab(ti)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        background: activeEditorTab === ti ? '#0073be' : '#fff',
                        color: activeEditorTab === ti ? '#fff' : '#4b5563',
                        border: activeEditorTab === ti ? '1px solid #0073be' : '1px solid #d1d5db',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {tab.label}
                    </button>
                    <button
                      type="button"
                      style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14 }}
                      onClick={() => setModules(ms => ms.map((m, i) => {
                        if (i !== idx) return m;
                        const tabs = (m.config?.tabs || []).filter((_, index) => index !== ti);
                        if (activeEditorTab === ti) setActiveEditorTab(-1);
                        else if (activeEditorTab > ti) setActiveEditorTab(activeEditorTab - 1);
                        return { ...m, config: { ...(m.config || {}), tabs } };
                      }))}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {(() => {
              const oTitle = mod.config?.title || '';
              const baseTabs = mod.config?.tabs || [];
              const isVirtualActive = oTitle === 'OFERTA SOT' && activeEditorTab === -1;

              if (isVirtualActive) {
                const allCards = baseTabs.reduce((acc, t) => [...acc, ...(t.cards || [])], []);
                return (
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic', background: '#f8fafc', padding: 8, borderRadius: 4 }}>
                      Aggregated Preview: All cards from the tabs below will be shown in a slider on the frontend.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                      {allCards.map((card, ci) => (
                        <div key={ci} style={{ padding: 8, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                            {card.image ? (
                              <img src={resolveUploadUrl(card.image)} style={{ width: '100%', aspectRatio: '1/1', objectFit: 'contain' }} alt="" />
                            ) : (
                              <div style={{ width: '100%', aspectRatio: '1/1', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#94a3b8' }}>No Image</div>
                            )}
                            <div style={{ fontSize: 10, fontWeight: 700, textAlign: 'center' }}>{card.buttonText || 'BLI TANI'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {allCards.length === 0 && <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 12 }}>No cards found in other tabs.</div>}
                  </div>
                );
              }

              const activeTabObj = baseTabs[activeEditorTab];

              if (activeTabObj) {
                return (
                  <div style={{ display: 'grid', gap: 12 }}>
                    <LocalInput
                      label="Tab Label"
                      value={activeTabObj.label}
                      onCommit={(v) => setModules(ms => ms.map((m, i) => {
                        if (i !== idx) return m;
                        const tabs = m.config.tabs.map((t, ti) => ti === activeEditorTab ? { ...t, label: v } : t);
                        return { ...m, config: { ...(m.config || {}), tabs } };
                      }))}
                    />

                    <div style={{ fontSize: 12, fontWeight: 700, marginTop: 8, color: '#64748b', textTransform: 'uppercase' }}>Cards (3 recommended)</div>
                    {(activeTabObj.cards || []).map((card, ci) => (
                      <SlideEditor
                        key={ci}
                        slide={card}
                        removeLabel="Remove Card"
                        onCommit={(updated) => setModules(ms => ms.map((m, i) => {
                          if (i !== idx) return m;
                          const tabs = m.config.tabs.map((t, ti) => {
                            if (ti !== activeEditorTab) return t;
                            const cards = (t.cards || []).map((c, index) => index === ci ? updated : c);
                            return { ...t, cards };
                          });
                          return { ...m, config: { ...(m.config || {}), tabs } };
                        }))}
                        onRemove={() => setModules(ms => ms.map((m, i) => {
                          if (i !== idx) return m;
                          const tabs = m.config.tabs.map((t, ti) => {
                            if (ti !== activeEditorTab) return t;
                            const cards = (t.cards || []).filter((_, index) => index !== ci);
                            return { ...t, cards };
                          });
                          return { ...m, config: { ...(m.config || {}), tabs } };
                        }))}
                        onUpload={(file) => handleFileUpload(idx, `config.tabs.${activeEditorTab}.cards.${ci}.image`, file)}
                        resolveUploadUrl={resolveUploadUrl}
                        LocalInput={LocalInput}
                        onPickMedia={onPickMedia}
                      />
                    ))}
                    <button
                      type="button"
                      className="admin-btn admin-btn-secondary admin-btn-xs"
                      onClick={() => setModules(ms => ms.map((m, i) => {
                        if (i !== idx) return m;
                        const tabs = m.config.tabs.map((t, ti) => {
                          if (ti !== activeEditorTab) return t;
                          return { ...t, cards: [...(t.cards || []), { image: '', link: '', buttonText: 'BLI TANI' }] };
                        });
                        return { ...m, config: { ...(m.config || {}), tabs } };
                      }))}
                    >
                      + Add Card
                    </button>
                  </div>
                );
              }
              return (
                <div style={{ textAlign: 'center', padding: 40, background: '#f8fafc', borderRadius: 8, color: '#64748b' }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Aggregation View</div>
                  <div style={{ fontSize: 12 }}>Change title to "OFERTA SOT" to see the slider preview.</div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {
        mod.type === 'hr-slider' && (
          <div style={{ display: 'grid', gap: 20 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <LocalInput
                  label="Module Title"
                  placeholder="e.g. PERSE TE BEHESH PJESE E GLOBE?"
                  value={(mod.config && mod.config.title) || ''}
                  onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...(m.config || {}), title: v } } : m))}
                />
              </div>
              <div style={{ paddingBottom: 6 }}>
                <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#003b49" />
              </div>
            </div>

            <div style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>SLIDES</span>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-xs"
                  onClick={() => setModules(ms => ms.map((m, i) => {
                    if (i !== idx) return m;
                    const slides = Array.isArray(m.config?.slides) ? m.config.slides : [];
                    return {
                      ...m,
                      config: {
                        ...(m.config || {}),
                        slides: [...slides, { image: '', title: 'New Slide', link: '' }]
                      }
                    };
                  }))}
                >
                  + Add Slide
                </button>
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                {((mod.config && Array.isArray(mod.config.slides)) ? mod.config.slides : []).map((s, si) => (
                  <SlideEditor
                    key={si}
                    slide={s}
                    onCommit={(updated) => setModules(ms => ms.map((m, i) => {
                      if (i !== idx) return m;
                      const slides = Array.isArray(m.config?.slides) ? m.config.slides : [];
                      return { ...m, config: { ...(m.config || {}), slides: slides.map((it, ii) => ii === si ? updated : it) } };
                    }))}
                    onRemove={() => setModules(ms => ms.map((m, i) => {
                      if (i !== idx) return m;
                      const slides = Array.isArray(m.config?.slides) ? m.config.slides : [];
                      return { ...m, config: { ...(m.config || {}), slides: slides.filter((_, ii) => ii !== si) } };
                    }))}
                    onUpload={(file) => handleFileUpload(idx, `config.slides.${si}.image`, file)}
                    resolveUploadUrl={resolveUploadUrl}
                    LocalInput={LocalInput}
                    onPickMedia={onPickMedia}
                  />
                ))}
              </div>
            </div>
          </div>
        )
      }

      {
        mod.type === 'home-oferta' && (
          <div style={{ display: 'grid', gap: 20 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <LocalInput
                  label="Section Title"
                  placeholder="e.g. Fletëpalosjet & Ofertat e fundit"
                  value={(mod.config && mod.config.title) || ''}
                  onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...(m.config || {}), title: v } } : m))}
                />
              </div>
              <div style={{ paddingBottom: 6 }}>
                <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <ColorPickerField label="Nav Button BG" value={mod.config.navBgColor} defaultColor="#0073be" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, navBgColor: v } } : m))} />
              <ColorPickerField label="Nav Button Text" value={mod.config.navTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, navTextColor: v } } : m))} />
            </div>

            <div style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#334155' }}>LEFT SLIDER</div>
              <div style={{ display: 'grid', gap: 12 }}>
                {((mod.config && Array.isArray(mod.config.leftSlides)) ? mod.config.leftSlides : []).map((s, si) => (
                  <SlideEditor
                    key={`left-${si}`}
                    slide={s}
                    onCommit={(updated) => setModules(ms => ms.map((m, i) => {
                      if (i !== idx) return m;
                      const slides = Array.isArray(m.config?.leftSlides) ? m.config.leftSlides : [];
                      return { ...m, config: { ...(m.config || {}), leftSlides: slides.map((it, ii) => ii === si ? updated : it) } };
                    }))}
                    onRemove={() => setModules(ms => ms.map((m, i) => {
                      if (i !== idx) return m;
                      const slides = Array.isArray(m.config?.leftSlides) ? m.config.leftSlides : [];
                      return { ...m, config: { ...(m.config || {}), leftSlides: slides.filter((_, ii) => ii !== si) } };
                    }))}
                    onUpload={(file) => handleFileUpload(idx, `config.leftSlides.${si}.image`, file)}
                    resolveUploadUrl={resolveUploadUrl}
                    LocalInput={LocalInput}
                    onPickMedia={onPickMedia}
                  />
                ))}
                <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setModules(ms => ms.map((m, i) => {
                  if (i !== idx) return m;
                  const slides = Array.isArray(m.config?.leftSlides) ? m.config.leftSlides : [];
                  return { ...m, config: { ...(m.config || {}), leftSlides: [...slides, { image: '', title: '', link: '' }] } };
                }))}>+ Add Left Slide</button>
              </div>
            </div>

            <div style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#334155' }}>RIGHT SLIDER</div>
              <div style={{ display: 'grid', gap: 12 }}>
                {((mod.config && Array.isArray(mod.config.rightSlides)) ? mod.config.rightSlides : []).map((s, si) => (
                  <SlideEditor
                    key={`right-${si}`}
                    slide={s}
                    onCommit={(updated) => setModules(ms => ms.map((m, i) => {
                      if (i !== idx) return m;
                      const slides = Array.isArray(m.config?.rightSlides) ? m.config.rightSlides : [];
                      return { ...m, config: { ...(m.config || {}), rightSlides: slides.map((it, ii) => ii === si ? updated : it) } };
                    }))}
                    onRemove={() => setModules(ms => ms.map((m, i) => {
                      if (i !== idx) return m;
                      const slides = Array.isArray(m.config?.rightSlides) ? m.config.rightSlides : [];
                      return { ...m, config: { ...(m.config || {}), rightSlides: slides.filter((_, ii) => ii !== si) } };
                    }))}
                    onUpload={(file) => handleFileUpload(idx, `config.rightSlides.${si}.image`, file)}
                    resolveUploadUrl={resolveUploadUrl}
                    LocalInput={LocalInput}
                    onPickMedia={onPickMedia}
                  />
                ))}
                <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setModules(ms => ms.map((m, i) => {
                  if (i !== idx) return m;
                  const slides = Array.isArray(m.config?.rightSlides) ? m.config.rightSlides : [];
                  return { ...m, config: { ...(m.config || {}), rightSlides: [...slides, { image: '', title: '', link: '' }] } };
                }))}>+ Add Right Slide</button>
              </div>
            </div>
          </div>
        )
      }

      {
        mod.type === 'offer-sliders' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <LocalInput
                  label="Section Title"
                  placeholder="e.g. Offers"
                  value={(mod.config && mod.config.title) || ''}
                  onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...(m.config || {}), title: v, items: Array.isArray(m.config?.items) ? m.config.items : [] } } : m))}
                />
              </div>
              <div style={{ paddingBottom: 6 }}>
                <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
              </div>
            </div>

            {((mod.config && Array.isArray(mod.config.items)) ? mod.config.items : []).map((s, si) => (
              <SlideEditor
                key={si}
                slide={s}
                removeLabel="Remove Offer"
                onCommit={(updated) => setModules(ms => ms.map((m, i) => {
                  if (i !== idx) return m;
                  const items = Array.isArray(m.config?.items) ? m.config.items : [];
                  return { ...m, config: { ...(m.config || {}), items: items.map((it, ii) => ii === si ? updated : it) } };
                }))}
                onRemove={() => setModules(ms => ms.map((m, i) => {
                  if (i !== idx) return m;
                  const items = Array.isArray(m.config?.items) ? m.config.items : [];
                  return { ...m, config: { ...(m.config || {}), items: items.filter((_, ii) => ii !== si) } };
                }))}
                onUpload={(file) => handleFileUpload(idx, `config.items.${si}.image`, file)}
                resolveUploadUrl={resolveUploadUrl}
                LocalInput={LocalInput}
                onPickMedia={onPickMedia}
              />
            ))}

            <button
              type="button"
              className="admin-btn admin-btn-secondary admin-btn-sm"
              onClick={() => setModules(ms => ms.map((m, i) => {
                if (i !== idx) return m;
                const items = Array.isArray(m.config?.items) ? m.config.items : [];
                return { ...m, config: { ...(m.config || {}), items: [...items, { image: '', title: '', link: '' }] } };
              }))}
            >
              + Add Offer
            </button>
          </div>
        )
      }

      {
        mod.type === 'products' && (
          <div>
            <LocalInput
              label="Module Title (optional)"
              placeholder="e.g. Popular Products"
              value={mod.config.title || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))}
              style={{ marginBottom: 12 }}
            />
            <ColorPickerField label="Title Color" value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} />
            <div className="admin-form-group">
              <label className="admin-label">Selection Mode</label>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input type="radio" checked={mod.config.mode === 'category'} onChange={() => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, mode: 'category' } } : m))} /> By Category</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input type="radio" checked={mod.config.mode === 'offer'} onChange={() => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, mode: 'offer' } } : m))} /> Offers</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input type="radio" checked={mod.config.mode === 'manual'} onChange={() => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, mode: 'manual' } } : m))} /> Manual IDs</label>
              </div>
            </div>
            {mod.config.mode === 'category' ? (
              <div style={{ display: 'grid', gap: 12 }}>
                <LocalSelectMultiple
                  label="Select Categories"
                  value={mod.config.categories || []}
                  options={flattenCategoryNames(categories)}
                  onCommit={(opts) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, categories: opts } } : m))}
                />
                <LocalNumberInput label="Limit" value={mod.config.limit ?? 8} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, limit: v } } : m))} style={{ width: 120 }} />
              </div>
            ) : mod.config.mode === 'manual' ? (
              <div style={{ display: 'grid', gap: 12 }}>
                <LocalInput
                  label="Product IDs"
                  placeholder="101, 102, 103..."
                  value={(mod.config.items || []).join(',')}
                  onCommit={(v) => {
                    const items = (v || '').split(',').map(s => Number(s.trim())).filter(Boolean);
                    setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, items } } : m));
                  }}
                />
                <LocalNumberInput label="Limit" value={mod.config.limit ?? 8} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, limit: v } } : m))} style={{ width: 120 }} />
              </div>
            ) : (
              <div>
                <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>Shows currently active offers automatically.</div>
                <LocalNumberInput label="Limit" value={mod.config.limit ?? 8} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, limit: v } } : m))} style={{ width: 120 }} />
              </div>
            )}

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8, textTransform: 'uppercase' }}>Advanced Filters (Backend)</div>
              <div style={{ display: 'grid', gap: 12 }}>
                <LocalSelectMultiple
                  label="Brands"
                  value={(mod.config.filters && mod.config.filters.brand) || []}
                  options={availableBrands.map(b => ({ value: b, label: b }))}
                  onCommit={(opts) => setModules(ms => ms.map((m, i) => i === idx ? {
                    ...m,
                    config: { ...m.config, filters: { ...(m.config.filters || {}), brand: opts } }
                  } : m))}
                />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <LocalNumberInput
                    label="Price Min"
                    value={Number((mod.config.filters && mod.config.filters.priceMin) || 0)}
                    onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? {
                      ...m,
                      config: { ...m.config, filters: { ...(m.config.filters || {}), priceMin: Number(v || 0) || '' } }
                    } : m))}
                  />
                  <LocalNumberInput
                    label="Price Max"
                    value={Number((mod.config.filters && mod.config.filters.priceMax) || 0)}
                    onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? {
                      ...m,
                      config: { ...m.config, filters: { ...(m.config.filters || {}), priceMax: Number(v || 0) || '' } }
                    } : m))}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <LocalSelect
                    label="Warranty"
                    value={(mod.config.filters && mod.config.filters.warranty) || ''}
                    options={[
                      { value: '', label: 'Any' },
                      { value: '12 muaj', label: '12 muaj' },
                      { value: '24 muaj', label: '24 muaj' },
                      { value: '36 muaj', label: '36 muaj' }
                    ]}
                    onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? {
                      ...m,
                      config: { ...m.config, filters: { ...(m.config.filters || {}), warranty: v } }
                    } : m))}
                  />
                  <LocalSelect
                    label="Rating Min"
                    value={(mod.config.filters && mod.config.filters.ratingMin) || ''}
                    options={[
                      { value: '', label: 'Any' },
                      { value: '5', label: '5' },
                      { value: '4', label: '4+' },
                      { value: '3', label: '3+' },
                      { value: '2', label: '2+' },
                      { value: '1', label: '1+' }
                    ]}
                    onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? {
                      ...m,
                      config: { ...m.config, filters: { ...(m.config.filters || {}), ratingMin: v } }
                    } : m))}
                  />
                </div>

                <div style={{ display: 'grid', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={!!(mod.config.filters && mod.config.filters.offer)}
                      onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? {
                        ...m,
                        config: { ...m.config, filters: { ...(m.config.filters || {}), offer: e.target.checked } }
                      } : m))}
                    />
                    Offer
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={!!(mod.config.filters && mod.config.filters.isNew)}
                      onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? {
                        ...m,
                        config: { ...m.config, filters: { ...(m.config.filters || {}), isNew: e.target.checked } }
                      } : m))}
                    />
                    New arrivals
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={!!(mod.config.filters && mod.config.filters.specialPrice)}
                      onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? {
                        ...m,
                        config: { ...m.config, filters: { ...(m.config.filters || {}), specialPrice: e.target.checked } }
                      } : m))}
                    />
                    Special price
                  </label>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {
        mod.type === 'blogs' && (
          <div>
            <LocalInput
              label="Module Title (optional)"
              placeholder="e.g. Latest News"
              value={mod.config.title || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))}
              style={{ marginBottom: 12 }}
            />
            <ColorPickerField label="Title Color" value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} />
            <div className="admin-form-group">
              <label className="admin-label">Selection Mode</label>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input type="radio" checked={mod.config.mode === 'category'} onChange={() => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, mode: 'category' } } : m))} /> By Category</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input type="radio" checked={mod.config.mode === 'manual'} onChange={() => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, mode: 'manual' } } : m))} /> Manual IDs</label>
              </div>
            </div>
            {mod.config.mode === 'category' ? (
              <div style={{ display: 'grid', gap: 12 }}>
                <LocalSelectMultiple
                  label="Select Blog Categories"
                  value={mod.config.categories || []}
                  options={flattenBlogCategoryNames(blogCategories).map((name) => ({ value: name, label: name }))}
                  onCommit={(opts) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, categories: opts } } : m))}
                />
                <LocalNumberInput label="Limit" value={mod.config.limit ?? 8} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, limit: v } } : m))} style={{ width: 120 }} />
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                <LocalInput
                  label="Blog IDs"
                  placeholder="1, 2, 3..."
                  value={(mod.config.items || []).join(',')}
                  onCommit={(v) => {
                    const items = (v || '').split(',').map(s => Number(s.trim())).filter(Boolean);
                    setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, items } } : m));
                  }}
                />
                <LocalNumberInput label="Limit" value={mod.config.limit ?? 8} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, limit: v } } : m))} style={{ width: 120 }} />
              </div>
            )}
          </div>
        )
      }

      {
        mod.type === 'split-banner' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ gridColumn: 'span 2' }}>
              <ColorPickerField label="Title Colors (If present)" value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} />
            </div>
            <div className="admin-card" style={{ padding: 12, marginBottom: 0 }}>
              <strong style={{ display: 'block', marginBottom: 12 }}>Left Box</strong>
              <div style={{ display: 'grid', gap: 12 }}>
                <LocalInput label="Image URL" value={mod.config.left?.image || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, left: { ...m.config.left, image: v } } } : m))} />
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                    Upload
                    <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.left.image', e.target.files[0]); }} />
                  </label>
                  <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, left: { ...m.config.left, image: url } } } : m))} />
                  {mod.config.left?.image ? <img src={resolveUploadUrl(mod.config.left.image)} alt="preview" style={{ height: 40, objectFit: 'cover' }} /> : null}
                </div>
                <LocalInput label="Alt Text" value={mod.config.left?.alt || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, left: { ...m.config.left, alt: v } } } : m))} />
                <LocalInput label="Link" value={mod.config.left?.link || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, left: { ...m.config.left, link: v } } } : m))} />
              </div>
            </div>
            <div className="admin-card" style={{ padding: 12, marginBottom: 0 }}>
              <strong style={{ display: 'block', marginBottom: 12 }}>Right Box</strong>
              <div style={{ display: 'grid', gap: 12 }}>
                <LocalInput label="Image URL" value={mod.config.right?.image || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, right: { ...m.config.right, image: v } } } : m))} />
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                    Upload
                    <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.right.image', e.target.files[0]); }} />
                  </label>
                  <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, right: { ...m.config.right, image: url } } } : m))} />
                  {mod.config.right?.image ? <img src={resolveUploadUrl(mod.config.right.image)} alt="preview" style={{ height: 40, objectFit: 'cover' }} /> : null}
                </div>
                <LocalInput label="Alt Text" value={mod.config.right?.alt || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, right: { ...m.config.right, alt: v } } } : m))} />
                <LocalInput label="Link" value={mod.config.right?.link || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, right: { ...m.config.right, link: v } } } : m))} />
              </div>
            </div>
          </div>
        )
      }

      {
        mod.type === 'promo-strip' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <LocalInput label="Main Text" value={mod.config.text || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, text: v } } : m))} />
              </div>
              <div style={{ paddingBottom: 6 }}>
                <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
              </div>
            </div>
            <LocalInput label="Description" value={mod.config.description || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, description: v } } : m))} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <LocalInput label="Link Text" value={mod.config.linkText || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, linkText: v } } : m))} />
                </div>
                <div style={{ paddingBottom: 6 }}>
                  <InlineColorPicker value={mod.config.linkColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, linkColor: v } } : m))} defaultColor="#0b79d0" />
                </div>
              </div>
              <LocalInput label="Link URL" value={mod.config.linkUrl || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, linkUrl: v } } : m))} />
            </div>
            <LocalInput
              label="Desktop Image URL"
              value={mod.config.imageDesktop || mod.config.image || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageDesktop: v } } : m))}
            />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                Upload Desktop
                <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.imageDesktop', e.target.files[0]); }} />
              </label>
              <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageDesktop: url } } : m))} />
              {(mod.config.imageDesktop || mod.config.image) ? <img src={resolveUploadUrl(mod.config.imageDesktop || mod.config.image)} alt="preview" style={{ height: 40, objectFit: 'cover' }} /> : null}
            </div>

            <LocalInput
              label="Mobile Image URL"
              value={mod.config.imageMobile || mod.config.image || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageMobile: v } } : m))}
            />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                Upload Mobile
                <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.imageMobile', e.target.files[0]); }} />
              </label>
              <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageMobile: url } } : m))} />
              {(mod.config.imageMobile || mod.config.image) ? <img src={resolveUploadUrl(mod.config.imageMobile || mod.config.image)} alt="preview" style={{ height: 40, objectFit: 'cover' }} /> : null}
            </div>
          </div>
        )
      }

      {
        mod.type === 'popup' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <LocalInput
                    label="Popup Title"
                    placeholder="Popup title (optional)"
                    value={mod.config.title || ''}
                    onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))}
                  />
                </div>
                <div style={{ paddingBottom: 6 }}>
                  <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#0f172a" />
                </div>
              </div>
              <div>
                <LocalNumberInput
                  label="Delay before showing (seconds)"
                  value={mod.config.delay || 0}
                  onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, delay: Number(v) || 0 } } : m))}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={mod.config.showBanner !== false}
                  onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, showBanner: e.target.checked } } : m))}
                />
                Show Banner
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={mod.config.showText !== false}
                  onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, showText: e.target.checked } } : m))}
                />
                Show Text
              </label>
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ fontSize: 12, color: '#64748b' }}>Popup Text</div>
              <TextEditor
                value={mod.config.text || ''}
                onCommit={(val) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, text: val } } : m))}
              />
            </div>

            <LocalInput
              label="Desktop Banner URL"
              value={mod.config.imageDesktop || mod.config.image || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageDesktop: v } } : m))}
            />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                Upload Desktop
                <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.imageDesktop', e.target.files[0]); }} />
              </label>
              <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageDesktop: url } } : m))} />
              {(mod.config.imageDesktop || mod.config.image) ? <img src={resolveUploadUrl(mod.config.imageDesktop || mod.config.image)} alt="preview" style={{ height: 40, objectFit: 'cover' }} /> : null}
            </div>

            <LocalInput
              label="Mobile Banner URL"
              value={mod.config.imageMobile || mod.config.image || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageMobile: v } } : m))}
            />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                Upload Mobile
                <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.imageMobile', e.target.files[0]); }} />
              </label>
              <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageMobile: url } } : m))} />
              {(mod.config.imageMobile || mod.config.image) ? <img src={resolveUploadUrl(mod.config.imageMobile || mod.config.image)} alt="preview" style={{ height: 40, objectFit: 'cover' }} /> : null}
            </div>

            <LocalInput
              label="Popup Link (Redirect when clicked)"
              placeholder="/products or https://..."
              value={mod.config.link || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, link: v } } : m))}
            />
          </div>
        )
      }

      {
        mod.type === 'categories' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <LocalInput label="Section Title" value={mod.config.title || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))} />
              </div>
              <div style={{ paddingBottom: 6 }}>
                <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
              </div>
            </div>
            <LocalSelectMultiple
              label="Categories to Show"
              value={mod.config.selected || []}
              options={categories.map(c => ({ value: c.name, label: c.name }))}
              onCommit={(opts) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, selected: opts } } : m))}
            />
            {(mod.config.selected && mod.config.selected.length > 0) && (
              <div style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Custom Category Images</div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {mod.config.selected.map(catName => (
                    <div key={catName} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f9fafb', padding: '6px 10px', borderRadius: 6, border: '1px solid #eee' }}>
                      <span style={{ fontSize: 13 }}>{catName}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {mod.config.customImages && mod.config.customImages[catName] ? (
                          <img src={resolveUploadUrl(mod.config.customImages[catName])} alt="custom" style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover', border: '1px solid #ddd' }} />
                        ) : null}
                        <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ padding: '2px 8px', fontSize: 11, height: 'auto' }}>
                          {mod.config.customImages && mod.config.customImages[catName] ? 'Change' : 'Upload'}
                          <input type="file" hidden accept="image/*" onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              handleFileUpload(idx, `config.customImages.${catName}`, e.target.files[0]);
                            }
                          }} />
                        </label>
                        <MediaBtn onPick={((cn) => (url) => {
                          setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, customImages: { ...(m.config.customImages || {}), [cn]: url } } } : m));
                        })(catName)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      }

      {
        mod.type === 'feature-promo' && (
          <div className="admin-card" style={{ padding: 12, marginBottom: 12 }}>
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <LocalInput label="Promo Title" value={mod.config.promoTitle || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoTitle: v } } : m))} />
                </div>
                <div style={{ paddingBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer', fontWeight: 'bold' }}>
                    <input type="checkbox" checked={!!mod.config.titleBold} onChange={(e) => { const checked = e.target.checked; setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleBold: checked } } : m)); }} />
                    B
                  </label>
                  <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <LocalInput label="Promo Subtitle" value={mod.config.promoSubtitle || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoSubtitle: v } } : m))} />
                </div>
                <div style={{ paddingBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer', fontWeight: 'bold' }}>
                    <input type="checkbox" checked={!!mod.config.subtitleBold} onChange={(e) => { const checked = e.target.checked; setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, subtitleBold: checked } } : m)); }} />
                    B
                  </label>
                  <InlineColorPicker value={mod.config.subtitleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, subtitleColor: v } } : m))} defaultColor="#1a1a1a" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <LocalInput label="Promo Text" value={mod.config.promoText || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoText: v } } : m))} />
                </div>
                <div style={{ paddingBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer', fontWeight: 'bold' }}>
                    <input type="checkbox" checked={!!mod.config.textBold} onChange={(e) => { const checked = e.target.checked; setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, textBold: checked } } : m)); }} />
                    B
                  </label>
                  <InlineColorPicker value={mod.config.textColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, textColor: v } } : m))} defaultColor="#475569" />
                </div>
              </div>
              <LocalInput label="Promo Image URL" value={mod.config.promoImage || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoImage: v } } : m))} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <LocalInput label="Promo Button Text" value={mod.config.promoButtonText || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoButtonText: v } } : m))} />
                <LocalInput label="Promo Button Link" value={mod.config.promoButtonLink || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoButtonLink: v } } : m))} />
              </div>

              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, marginTop: 12 }}>
                Button Colors:
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <ColorPickerField label="Button BG" value={mod.config.buttonBgColor} defaultColor="#f97316" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonBgColor: v } } : m))} />
                <ColorPickerField label="Button Text" value={mod.config.buttonTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonTextColor: v } } : m))} />
                <ColorPickerField label="Hover BG" value={mod.config.buttonHoverBgColor} defaultColor="#ea580c" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverBgColor: v } } : m))} />
                <ColorPickerField label="Hover Text" value={mod.config.buttonHoverTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverTextColor: v } } : m))} />
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                  Upload
                  <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.promoImage', e.target.files[0]); }} />
                </label>
                <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoImage: url } } : m))} />
                {mod.config.promoImage ? <img src={resolveUploadUrl(mod.config.promoImage)} alt="preview" style={{ height: 40, objectFit: 'cover' }} /> : null}
              </div>
            </div>
          </div>
        )
      }

      {
        mod.type === 'feature-promo-reverse' && (
          <div className="admin-card" style={{ padding: 12, marginBottom: 12 }}>
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <LocalInput label="Promo Title" value={mod.config.promoTitle || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoTitle: v } } : m))} />
                </div>
                <div style={{ paddingBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer', fontWeight: 'bold' }}>
                    <input type="checkbox" checked={!!mod.config.titleBold} onChange={(e) => { const checked = e.target.checked; setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleBold: checked } } : m)); }} />
                    B
                  </label>
                  <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <LocalInput label="Promo Subtitle" value={mod.config.promoSubtitle || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoSubtitle: v } } : m))} />
                </div>
                <div style={{ paddingBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer', fontWeight: 'bold' }}>
                    <input type="checkbox" checked={!!mod.config.subtitleBold} onChange={(e) => { const checked = e.target.checked; setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, subtitleBold: checked } } : m)); }} />
                    B
                  </label>
                  <InlineColorPicker value={mod.config.subtitleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, subtitleColor: v } } : m))} defaultColor="#1a1a1a" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <LocalInput label="Promo Text" value={mod.config.promoText || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoText: v } } : m))} />
                </div>
                <div style={{ paddingBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer', fontWeight: 'bold' }}>
                    <input type="checkbox" checked={!!mod.config.textBold} onChange={(e) => { const checked = e.target.checked; setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, textBold: checked } } : m)); }} />
                    B
                  </label>
                  <InlineColorPicker value={mod.config.textColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, textColor: v } } : m))} defaultColor="#475569" />
                </div>
              </div>
              <LocalInput label="Promo Image URL" value={mod.config.promoImage || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoImage: v } } : m))} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <LocalInput label="Promo Button Text" value={mod.config.promoButtonText || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoButtonText: v } } : m))} />
                <LocalInput label="Promo Button Link" value={mod.config.promoButtonLink || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoButtonLink: v } } : m))} />
              </div>

              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, marginTop: 12 }}>
                Button Colors:
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <ColorPickerField label="Button BG" value={mod.config.buttonBgColor} defaultColor="#f97316" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonBgColor: v } } : m))} />
                <ColorPickerField label="Button Text" value={mod.config.buttonTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonTextColor: v } } : m))} />
                <ColorPickerField label="Hover BG" value={mod.config.buttonHoverBgColor} defaultColor="#ea580c" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverBgColor: v } } : m))} />
                <ColorPickerField label="Hover Text" value={mod.config.buttonHoverTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverTextColor: v } } : m))} />
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                  Upload
                  <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.promoImage', e.target.files[0]); }} />
                </label>
                <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, promoImage: url } } : m))} />
                {mod.config.promoImage ? <img src={resolveUploadUrl(mod.config.promoImage)} alt="preview" style={{ height: 40, objectFit: 'cover' }} /> : null}
              </div>
            </div>
          </div>
        )
      }

      {
        mod.type === 'announcement' && (
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 4 }}>
              <div style={{ flex: 1 }}>
                <LocalInput
                  label="Speed (seconds)"
                  value={String(mod.config.speed || 5)}
                  onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...(m.config || {}), speed: Number(v || 0) } } : m))}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              {(() => {
                const items = mod.config.items || (mod.config.text ? [{ text: mod.config.text, linkText: mod.config.linkText, linkUrl: mod.config.linkUrl }] : []);
                return items.map((item, itemIdx) => {
                  const updateItem = (key, val) => {
                    setModules(ms => ms.map((m, i) => {
                      if (i !== idx) return m;
                      const newItems = [...items];
                      newItems[itemIdx] = { ...newItems[itemIdx], [key]: val };
                      return { ...m, config: { ...m.config, items: newItems } };
                    }));
                  };
                  return (
                    <div key={itemIdx} style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Announcement {itemIdx + 1}</div>
                        <button type="button" className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => {
                          setModules(ms => ms.map((m, i) => {
                            if (i !== idx) return m;
                            return { ...m, config: { ...m.config, items: items.filter((_, ii) => ii !== itemIdx) } };
                          }));
                        }}>Remove</button>
                      </div>
                      <div style={{ display: 'grid', gap: 12 }}>
                        <TextEditor
                          value={item.text || ''}
                          onCommit={(val) => updateItem('text', val)}
                        />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <LocalInput label="Link Text (Optional)" value={item.linkText || ''} onCommit={(v) => updateItem('linkText', v)} />
                          <LocalInput label="Link URL" value={item.linkUrl || ''} onCommit={(v) => updateItem('linkUrl', v)} />
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            <button
              type="button"
              className="admin-btn admin-btn-secondary admin-btn-sm"
              onClick={() => setModules(ms => ms.map((m, i) => {
                if (i !== idx) return m;
                const items = m.config.items || (m.config.text ? [{ text: m.config.text, linkText: m.config.linkText, linkUrl: m.config.linkUrl }] : []);
                return { ...m, config: { ...(m.config || {}), items: [...items, { text: '', linkText: '', linkUrl: '' }] } };
              }))}
            >
              + Add Announcement
            </button>
          </div>
        )
      }

      {
        mod.type === 'contact-form' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <LocalInput
                  label="Form Title"
                  value={mod.config.title || ''}
                  onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))}
                />
              </div>
              <div style={{ paddingBottom: 6 }}>
                <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
              </div>
            </div>
            <LocalInput
              label="Submit Button Text"
              value={mod.config.submitText || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, submitText: v } } : m))}
            />
            <LocalInput
              label="Receiving Email (Optional - overrides default support email)"
              value={mod.config.receivingEmail || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, receivingEmail: v } } : m))}
            />

            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, marginTop: 12 }}>
              Button Colors:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <ColorPickerField label="Button BG" value={mod.config.buttonBgColor} defaultColor="#f97316" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonBgColor: v } } : m))} />
              <ColorPickerField label="Button Text" value={mod.config.buttonTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonTextColor: v } } : m))} />
              <ColorPickerField label="Hover BG" value={mod.config.buttonHoverBgColor} defaultColor="#ea580c" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverBgColor: v } } : m))} />
              <ColorPickerField label="Hover Text" value={mod.config.buttonHoverTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverTextColor: v } } : m))} />
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              {(mod.config.fields || []).map((f, fi) => (
                <div key={f.id || fi} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#fff' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <LocalInput
                      label="Label"
                      value={f.label || ''}
                      onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, fields: (m.config.fields || []).map((x, xi) => xi === fi ? { ...x, label: v } : x) } } : m))}
                    />
                    <LocalInput
                      label="Name"
                      value={f.name || ''}
                      onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, fields: (m.config.fields || []).map((x, xi) => xi === fi ? { ...x, name: v } : x) } } : m))}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
                    <LocalSelect
                      label="Type"
                      value={f.type || 'text'}
                      options={[
                        { value: 'text', label: 'Text' },
                        { value: 'email', label: 'Email' },
                        { value: 'tel', label: 'Phone' },
                        { value: 'textarea', label: 'Textarea' },
                      ]}
                      onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, fields: (m.config.fields || []).map((x, xi) => xi === fi ? { ...x, type: v } : x) } } : m))}
                    />
                    <LocalInput
                      label="Placeholder"
                      value={f.placeholder || ''}
                      onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, fields: (m.config.fields || []).map((x, xi) => xi === fi ? { ...x, placeholder: v } : x) } } : m))}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={!!f.required}
                        onChange={(e) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, fields: (m.config.fields || []).map((x, xi) => xi === fi ? { ...x, required: e.target.checked } : x) } } : m))}
                      />
                      Required
                    </label>
                    <button
                      type="button"
                      className="admin-btn admin-btn-danger admin-btn-sm"
                      onClick={() => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, fields: (m.config.fields || []).filter((_, xi) => xi !== fi) } } : m))}
                    >
                      Remove Field
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-sm"
                onClick={() => setModules(ms => ms.map((m, i) => {
                  if (i !== idx) return m;
                  const next = [...(m.config.fields || [])];
                  next.push({ id: `field_${Date.now()}`, name: '', label: 'New Field', type: 'text', placeholder: '', required: false });
                  return { ...m, config: { ...m.config, fields: next } };
                }))}
              >
                + Add Field
              </button>
            </div>
          </div>
        )
      }




      {/* OFERTA MODULE EDITORS */}
      {
        mod.type === 'offer-hero' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <LocalInput label="Title Part 1" value={mod.config.titlePart1 || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titlePart1: v } } : m))} />
                <LocalInput label="Title Part 2" value={mod.config.titlePart2 || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titlePart2: v } } : m))} />
                <LocalInput label="Title Part 3" value={mod.config.titlePart3 || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titlePart3: v } } : m))} />
              </div>
              <div style={{ paddingBottom: 6 }}>
                <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
              </div>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <LocalInput label="Image URL" value={mod.config.image || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, image: v } } : m))} />
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                  Upload Image
                  <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.image', e.target.files[0]); }} />
                </label>
                <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, image: url } } : m))} />
                {mod.config.image ? <img src={resolveUploadUrl(mod.config.image)} alt="preview" style={{ height: 60, objectFit: 'cover' }} /> : null}
              </div>
            </div>
          </div>
        )
      }

      {
        mod.type === 'offer-featured' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <LocalInput label="Section Title" value={mod.config.title || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))} />
              </div>
              <div style={{ paddingBottom: 6 }}>
                <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
              </div>
            </div>

            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, marginTop: 12 }}>
              Buttons Global Colors:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <ColorPickerField label="Button BG" value={mod.config.buttonBgColor} defaultColor="#f97316" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonBgColor: v } } : m))} />
              <ColorPickerField label="Button Text" value={mod.config.buttonTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonTextColor: v } } : m))} />
              <ColorPickerField label="Hover BG" value={mod.config.buttonHoverBgColor} defaultColor="#ea580c" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverBgColor: v } } : m))} />
              <ColorPickerField label="Hover Text" value={mod.config.buttonHoverTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverTextColor: v } } : m))} />
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, marginTop: 12 }}>
              Section Categories (Tabs):
            </div>
            <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
              {[0, 1, 2].map(catIdx => {
                const cat = (mod.config.categories && mod.config.categories[catIdx]) || {};
                const updateCat = (key, val) => {
                  setModules(ms => ms.map((m, i) => {
                    if (i !== idx) return m;
                    const newCats = [...(m.config.categories || [])];
                    while (newCats.length <= catIdx) newCats.push({});
                    newCats[catIdx] = { ...newCats[catIdx], [key]: val };
                    return { ...m, config: { ...m.config, categories: newCats } };
                  }));
                };
                return (
                  <div key={catIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <LocalInput label={`Tab ${catIdx + 1} Label`} value={cat.label || ''} onCommit={(v) => updateCat('label', v)} />
                    <LocalInput label="Link" value={cat.link || ''} onCommit={(v) => updateCat('link', v)} />
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'grid', gap: 16 }}>
              {[0, 1, 2].map(cardIdx => {
                const card = (mod.config.cards && mod.config.cards[cardIdx]) || {};
                const updateCard = (key, val) => {
                  setModules(ms => ms.map((m, i) => {
                    if (i !== idx) return m;
                    const newCards = [...(m.config.cards || [])];
                    // Ensure we have objects up to this index
                    while (newCards.length <= cardIdx) newCards.push({});
                    newCards[cardIdx] = { ...newCards[cardIdx], [key]: val };
                    return { ...m, config: { ...m.config, cards: newCards } };
                  }));
                };

                return (
                  <div key={cardIdx} style={{ padding: 10, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Card {cardIdx + 1}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <LocalInput label="Button Text" value={card.buttonText || ''} onCommit={(v) => updateCard('buttonText', v)} />
                      <LocalInput label="Link URL" value={card.link || ''} onCommit={(v) => updateCard('link', v)} />
                    </div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      <LocalInput label="Image URL" value={card.image || ''} onCommit={(v) => updateCard('image', v)} />
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                          Upload Image
                          <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, `config.cards.${cardIdx}.image`, e.target.files[0], true); }} />
                        </label>
                        <MediaBtn onPick={((ci) => (url) => {
                          setModules(ms => ms.map((m, i) => {
                            if (i !== idx) return m;
                            const newCards = [...(m.config.cards || [])];
                            while (newCards.length <= ci) newCards.push({});
                            newCards[ci] = { ...newCards[ci], image: url };
                            return { ...m, config: { ...m.config, cards: newCards } };
                          }));
                        })(cardIdx)} />
                        {card.image ? <img src={resolveUploadUrl(card.image)} alt="preview" style={{ height: 40, objectFit: 'contain' }} /> : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      }

      {
        mod.type === 'offer-catalogs' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <LocalInput label="Title" value={mod.config.title || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))} />
              </div>
              <div style={{ paddingBottom: 6 }}>
                <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
              </div>
            </div>
            <LocalInput label="Sticker Text" value={mod.config.sticker || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, sticker: v } } : m))} />
            {/* Helper to add catalog items would go here. For now, simple explanation. */}
            <div style={{ fontSize: 13, color: '#64748b' }}>
              Renders active catalogs relative to current date.
            </div>
          </div>
        )
      }

      {
        mod.type === 'offer-needs' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <LocalInput label="Title" value={mod.config.title || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))} />
              </div>
              <div style={{ paddingBottom: 6 }}>
                <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
              </div>
            </div>

            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, marginTop: 12 }}>
              Buttons Global Colors:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <ColorPickerField label="Button BG" value={mod.config.buttonBgColor} defaultColor="#f97316" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonBgColor: v } } : m))} />
              <ColorPickerField label="Button Text" value={mod.config.buttonTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonTextColor: v } } : m))} />
              <ColorPickerField label="Hover BG" value={mod.config.buttonHoverBgColor} defaultColor="#ea580c" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverBgColor: v } } : m))} />
              <ColorPickerField label="Hover Text" value={mod.config.buttonHoverTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverTextColor: v } } : m))} />
            </div>

            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, marginTop: 12 }}>
              Buttons Configuration:
            </div>
            <div style={{ display: 'grid', gap: 16 }}>
              {/* Provide exactly 3 buttons for now as per previous defaults or dynamic if we want, but default is 3 */}
              {([0, 1, 2]).map(btnIdx => {
                const item = (mod.config.items && mod.config.items[btnIdx]) || {};
                const updateItem = (key, val) => {
                  setModules(ms => ms.map((m, i) => {
                    if (i !== idx) return m;
                    const newItems = [...(m.config.items || [])];
                    while (newItems.length <= btnIdx) newItems.push({});
                    newItems[btnIdx] = { ...newItems[btnIdx], [key]: val };
                    return { ...m, config: { ...m.config, items: newItems } };
                  }));
                };
                return (
                  <div key={btnIdx} style={{ padding: 10, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Button {btnIdx + 1}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <LocalInput label="Button Label" value={item.label || ''} onCommit={(v) => updateItem('label', v)} />
                      <LocalInput label="Link URL" value={item.link || ''} onCommit={(v) => updateItem('link', v)} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      }

      {
        mod.type === 'offer-footer' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <LocalInput label="Main Text" value={mod.config.text || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, text: v } } : m))} />
              </div>
              <div style={{ paddingBottom: 6 }}>
                <InlineColorPicker value={mod.config.titleColor} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} defaultColor="#1a1a1a" />
              </div>
            </div>

            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, marginTop: 12 }}>
              Button Global Colors:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <ColorPickerField label="Button BG" value={mod.config.buttonBgColor} defaultColor="#f97316" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonBgColor: v } } : m))} />
              <ColorPickerField label="Button Text" value={mod.config.buttonTextColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonTextColor: v } } : m))} />
              <ColorPickerField label="Hover BG" value={mod.config.buttonHoverBgColor} defaultColor="#ffffff" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverBgColor: v } } : m))} />
              <ColorPickerField label="Hover Text" value={mod.config.buttonHoverTextColor} defaultColor="#f97316" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonHoverTextColor: v } } : m))} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <LocalInput label="Button Text" value={mod.config.buttonText || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonText: v } } : m))} />
              <LocalInput label="Button Link" value={mod.config.buttonLink || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, buttonLink: v } } : m))} />
            </div>
          </div>
        )
      }
      {
        mod.type === 'metodat-pagese' && (
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Global Appearance Colors:</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
              <ColorPickerField label="Title Color" value={mod.config.titleColor} defaultColor="#002f4a" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} />
              <ColorPickerField label="Description Color" value={mod.config.descColor} defaultColor="#475569" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, descColor: v } } : m))} />
              <ColorPickerField label="Dot Color" value={mod.config.dotColor} defaultColor="#9ca3af" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, dotColor: v } } : m))} />
              <ColorPickerField label="Row Background" value={mod.config.bgColor} defaultColor="#f8fafc" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, bgColor: v } } : m))} />
            </div>

            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginTop: 8 }}>Payment Methods (Accordion):</div>
            <div style={{ display: 'grid', gap: 12 }}>
              {(mod.config.items || []).map((item, itemIdx) => {
                const updateItem = (key, val) => {
                  setModules(ms => ms.map((m, i) => {
                    if (i !== idx) return m;
                    const newItems = [...(m.config.items || [])];
                    newItems[itemIdx] = { ...newItems[itemIdx], [key]: val };
                    return { ...m, config: { ...m.config, items: newItems } };
                  }));
                };
                return (
                  <div key={itemIdx} style={{ padding: 12, background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Method {itemIdx + 1}</div>
                      <button type="button" className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => {
                        setModules(ms => ms.map((m, i) => {
                          if (i !== idx) return m;
                          return { ...m, config: { ...m.config, items: (m.config.items || []).filter((_, ii) => ii !== itemIdx) } };
                        }));
                      }}>Remove</button>
                    </div>
                    <LocalInput label="Title (e.g. Cash)" value={item.title || ''} onCommit={(v) => updateItem('title', v)} />
                    <div className="admin-form-group" style={{ marginBottom: 0 }}>
                      <label className="admin-label">Description</label>
                      <textarea
                        className="admin-input"
                        rows={2}
                        value={item.description || ''}
                        onChange={(e) => updateItem('description', e.target.value)}
                      />
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-sm"
                onClick={() => setModules(ms => ms.map((m, i) => {
                  if (i !== idx) return m;
                  const items = Array.isArray(m.config?.items) ? m.config.items : [];
                  return { ...m, config: { ...(m.config || {}), items: [...items, { title: 'New Method', description: 'Description here...' }] } };
                }))}
              >
                + Add Method
              </button>
            </div>
          </div>
        )
      }


      {
        mod.type === 'double-image-movement' && (
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Left Image */}
              <div style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#334155' }}>LEFT IMAGE (Main)</div>
                <LocalInput label="Image URL" value={mod.config.imageLeft || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageLeft: v } } : m))} />
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
                  <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                    Upload
                    <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.imageLeft', e.target.files[0]); }} />
                  </label>
                  <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageLeft: url } } : m))} />
                  {mod.config.imageLeft ? <img src={resolveUploadUrl(mod.config.imageLeft)} alt="left preview" style={{ height: 40, width: 40, objectFit: 'cover', borderRadius: 4 }} /> : null}
                </div>
                <div style={{ marginTop: 8 }}>
                  <LocalInput label="Link URL" value={mod.config.linkLeft || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, linkLeft: v } } : m))} />
                </div>
              </div>

              {/* Right Image */}
              <div style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#334155' }}>RIGHT IMAGE (Side)</div>
                <LocalInput label="Image URL" value={mod.config.imageRight || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageRight: v } } : m))} />
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
                  <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flexShrink: 0 }}>
                    Upload
                    <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(idx, 'config.imageRight', e.target.files[0]); }} />
                  </label>
                  <MediaBtn onPick={(url) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, imageRight: url } } : m))} />
                  {mod.config.imageRight ? <img src={resolveUploadUrl(mod.config.imageRight)} alt="right preview" style={{ height: 40, width: 40, objectFit: 'cover', borderRadius: 4 }} /> : null}
                </div>
                <div style={{ marginTop: 8 }}>
                  <LocalInput label="Link URL" value={mod.config.linkRight || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, linkRight: v } } : m))} />
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
              <LocalNumberInput
                label="Split Percentage (Left Image %)"
                value={mod.config.splitPercent || 80}
                onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, splitPercent: Number(v) || 80 } } : m))}
              />
              <LocalSelect
                label="Moving Image"
                value={mod.config.movingImage || 'both'}
                options={[
                  { value: 'left', label: 'Left Image' },
                  { value: 'right', label: 'Right Image' },
                  { value: 'both', label: 'Both Images' },
                ]}
                onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, movingImage: v } } : m))}
              />
              <LocalSelect
                label="Movement Direction"
                value={mod.config.direction || 'right'}
                options={[
                  { value: 'left', label: 'Left' },
                  { value: 'right', label: 'Right' },
                  { value: 'top', label: 'Top' },
                  { value: 'bottom', label: 'Bottom' },
                ]}
                onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, direction: v } } : m))}
              />
              <LocalSelect
                label="Top Image (Z-Index)"
                value={mod.config.topImage || 'left'}
                options={[
                  { value: 'left', label: 'Left Image on Top' },
                  { value: 'right', label: 'Right Image on Top' },
                ]}
                onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, topImage: v } } : m))}
              />
              <LocalSelect
                label="Interaction Trigger"
                value={mod.config.trigger || 'hover'}
                options={[
                  { value: 'hover', label: 'On Hover' },
                  { value: 'time', label: 'Based on Time (Auto)' },
                  { value: 'click', label: 'On Click' },
                  { value: 'scroll', label: 'On Scroll (scrolled into view)' },
                ]}
                onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, trigger: v } } : m))}
              />
              <LocalSelect
                label="Effect Kind"
                value={mod.config.effect || 'slide'}
                options={[
                  { value: 'slide', label: 'Slide' },
                  { value: 'fade', label: 'Fade' },
                  { value: 'zoom', label: 'Zoom' },
                ]}
                onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, effect: v } } : m))}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <LocalNumberInput
                label="Transition Speed (seconds)"
                value={mod.config.transitionSpeed || 0.5}
                onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, transitionSpeed: Number(v) || 0.5 } } : m))}
              />
              {mod.config.trigger === 'time' && (
                <LocalNumberInput
                  label="Auto Interval (ms)"
                  value={mod.config.autoInterval || 3000}
                  onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, autoInterval: Number(v) || 3000 } } : m))}
                />
              )}
            </div>
          </div>
        )
      }

      {
        mod.type === 'brands' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <LocalInput label="Section Title" value={mod.config.title || ''} onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))} />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <ColorPickerField
                label="Nav Button BG"
                value={mod.config.navBgColor}
                defaultColor="#dededeff"
                onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, navBgColor: v } } : m))}
              />
              <ColorPickerField
                label="Nav Button Hover BG"
                value={mod.config.navHoverBgColor}
                defaultColor="#0891b2"
                onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, navHoverBgColor: v } } : m))}
              />
            </div>

            {(() => {
              const orderedBrandNames = (() => {
                const customOrder = (mod.config && Array.isArray(mod.config.brandOrder)) ? mod.config.brandOrder : null;
                if (!customOrder || !customOrder.length) return availableBrands;

                const result = [];
                const added = new Set();
                customOrder.forEach(name => {
                  if (availableBrands.includes(name) && !added.has(name)) {
                    result.push(name);
                    added.add(name);
                  }
                });
                availableBrands.forEach(name => {
                  if (!added.has(name)) {
                    result.push(name);
                    added.add(name);
                  }
                });
                return result;
              })();

              return availableBrands.length ? (
                <div style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>Brand Logos & Order</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>Use ▲ and ▼ to reorder brands in this slider. Logos are saved globally.</div>
                    </div>
                    {mod.config.brandOrder && (
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        onClick={() => {
                          setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, brandOrder: null } } : m));
                        }}
                        style={{ fontSize: 11, padding: '2px 8px' }}
                      >
                        Reset Order
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {orderedBrandNames.map((brandName, bIdx) => {
                      const lower = String(brandName || '').toLowerCase();
                      const current = (globalBrandLogos && (globalBrandLogos[brandName] || globalBrandLogos[lower])) || (() => {
                        if (!globalBrandLogos || typeof globalBrandLogos !== 'object') return '';
                        const key = Object.keys(globalBrandLogos).find((k) => String(k).toLowerCase() === lower);
                        return key ? globalBrandLogos[key] : '';
                      })();

                      const setLogo = async (url) => {
                        const token = getToken();
                        try {
                          const trimmed = String(url || '').trim();
                          if (!trimmed) {
                            await deleteBrandLogo(brandName, token);
                            setGlobalBrandLogos((prev) => {
                              const next = { ...(prev || {}) };
                              delete next[brandName];
                              return next;
                            });
                            return;
                          }
                          const res = await setBrandLogoUrl(brandName, trimmed, token);
                          if (res && res.url) {
                            setGlobalBrandLogos((prev) => ({ ...(prev || {}), [brandName]: res.url }));
                          }
                        } catch (e) {
                          console.error(e);
                          toast.error('Gabim gjatë ruajtjes së logos');
                        }
                      };
                      const uploadLogo = async (file) => {
                        if (!file) return;
                        try {
                          const token = getToken();
                          const res = await uploadBrandLogo(brandName, file, token);
                          if (res && res.url) {
                            setGlobalBrandLogos((prev) => ({ ...(prev || {}), [brandName]: res.url }));
                            toast.success('Imazhi u ngarkua');
                          } else {
                            toast.error('Ngarkimi dështoi');
                          }
                        } catch (e) {
                          console.error(e);
                          toast.error('Gabim gjatë ngarkimit');
                        }
                      };

                      return (
                        <div key={brandName} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f9fafb', padding: '6px 10px', borderRadius: 6, border: '1px solid #eee', gap: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <button
                                type="button"
                                disabled={bIdx === 0}
                                onClick={() => {
                                  if (bIdx <= 0) return;
                                  const nextOrder = [...orderedBrandNames];
                                  const temp = nextOrder[bIdx];
                                  nextOrder[bIdx] = nextOrder[bIdx - 1];
                                  nextOrder[bIdx - 1] = temp;
                                  setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, brandOrder: nextOrder } } : m));
                                }}
                                style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 3, width: 22, height: 18, cursor: bIdx === 0 ? 'not-allowed' : 'pointer', opacity: bIdx === 0 ? 0.3 : 1, fontSize: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                title="Move Up"
                              >
                                ▲
                              </button>
                              <button
                                type="button"
                                disabled={bIdx === orderedBrandNames.length - 1}
                                onClick={() => {
                                  if (bIdx >= orderedBrandNames.length - 1) return;
                                  const nextOrder = [...orderedBrandNames];
                                  const temp = nextOrder[bIdx];
                                  nextOrder[bIdx] = nextOrder[bIdx + 1];
                                  nextOrder[bIdx + 1] = temp;
                                  setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, brandOrder: nextOrder } } : m));
                                }}
                                style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 3, width: 22, height: 18, cursor: bIdx === orderedBrandNames.length - 1 ? 'not-allowed' : 'pointer', opacity: bIdx === orderedBrandNames.length - 1 ? 0.3 : 1, fontSize: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                title="Move Down"
                              >
                                ▼
                              </button>
                            </div>
                            <span style={{ fontSize: 13, minWidth: 120, fontWeight: 500 }}>{bIdx + 1}. {brandName}</span>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
                            {current ? (
                              <img src={resolveUploadUrl(current)} alt="logo" style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'contain', border: '1px solid #ddd', background: '#fff' }} />
                            ) : null}

                            <div style={{ minWidth: 240 }}>
                              <LocalInput
                                label={null}
                                placeholder="Logo URL (optional)"
                                value={current}
                                onCommit={(v) => setLogo(v)}
                                style={{ marginBottom: 0 }}
                              />
                            </div>

                            <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ padding: '2px 8px', fontSize: 11, height: 'auto', flexShrink: 0 }}>
                              {current ? 'Change' : 'Upload'}
                              <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) uploadLogo(e.target.files[0]); }} />
                            </label>
                            <MediaBtn onPick={((bn) => (url) => setLogo(url))(brandName)} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#64748b' }}>No brands found (check products data).</div>
              );
            })()}
          </div>
        )
      }

      {mod.type === 'dyqani-afer-teje' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <LocalInput
            label="Module Title"
            value={mod.config.title || ''}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))}
          />
          <ColorPickerField label="Title Color" value={mod.config.titleColor} defaultColor="#1a1a1a" onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))} />
        </div>
      )}

      {mod.type === 'bundle-offer' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <LocalInput
            label="Titulli i Modulit"
            value={mod.config.title || ''}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, title: v } } : m))}
          />
          <ColorPickerField
            label="Ngjyra e Titullit"
            value={mod.config.titleColor}
            defaultColor="#003b49"
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, titleColor: v } } : m))}
          />
          <LocalSelect
            label="Mënyra e Limitimit"
            value={mod.config.limitMode || 'all'}
            options={[
              { value: 'all', label: 'Të gjitha produktet' },
              { value: 'category', label: 'Sipas Kategorisë' },
              { value: 'single_product', label: 'Produkt i vetëm' }
            ]}
            onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, limitMode: v } } : m))}
          />
          {mod.config.limitMode === 'category' && (
            <LocalSelectMultiple
              label="Zgjidh Kategoritë"
              value={mod.config.categories || []}
              options={flattenCategoryNames(categories)}
              onCommit={(opts) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, categories: opts } } : m))}
            />
          )}
          {mod.config.limitMode === 'single_product' && (
            <LocalInput
              label="ID e Produktit Kryesor (Single Product ID)"
              placeholder="Vendosni ID e produktit (p.sh. 123)"
              value={mod.config.singleProductId || ''}
              onCommit={(v) => setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, singleProductId: v } } : m))}
            />
          )}
        </div>
      )}

      {mod.type === 'old-to-new-offer' && (() => {
        const OTN_CATEGORY_LIST = [
          { id: '3031', label: 'Elektroshtepiake te medha' },
          { id: '3032', label: 'Elektroshtepiake te vogla' },
          { id: '3033', label: 'Telefonia' },
          { id: '3034', label: 'TV & Audio' },
          { id: '3035', label: 'Ftohje & Ngrohje' },
          { id: '3036', label: 'Kompjutera' },
          { id: '3037', label: 'Kujdesi Personal' },
          { id: '3038', label: 'Pastrimi & Hekurosja' },
          { id: '2812', label: 'Foto & Video' },
          { id: '2813', label: 'Lojra & Hobi' },
          { id: '2814', label: 'Siguria & Mbikqyrja' },
          { id: '2815', label: 'Bateri & Llampa' },
        ];
        const discounts = mod.config.categoryDiscounts || {};
        return (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', paddingBottom: 4, borderBottom: '1px solid #e2e8f0', marginBottom: 4 }}>
              ♻️ Zbritjet për "Nga e Vjetra në të Re" (%)
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
              Vendos përqindjen e zbritjes për çdo kategori kryesore. Zbritja aplikohet vetëm kur klienti vjen nga faqja e kampanjës OldToNew.
            </div>
            {OTN_CATEGORY_LIST.map(cat => (
              <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: 1, fontSize: 12, color: '#475569', fontWeight: 600 }}>{cat.label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    defaultValue={discounts[cat.id] ?? 0}
                    onBlur={(e) => {
                      const val = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                      e.target.value = val;
                      const newDiscounts = { ...discounts, [cat.id]: val };
                      setModules(ms => ms.map((m, i) => i === idx ? { ...m, config: { ...m.config, categoryDiscounts: newDiscounts } } : m));
                    }}
                    style={{
                      width: 64,
                      padding: '4px 8px',
                      border: '1px solid #cbd5e1',
                      borderRadius: 6,
                      fontSize: 12,
                      textAlign: 'center',
                      color: '#1e293b',
                      background: '#f8fafc'
                    }}
                  />
                  <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700 }}>%</span>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

    </div >
  );
}

export function SlideEditor({ slide, onCommit, onRemove, onUpload, onUploadMobile, removeLabel, resolveUploadUrl, LocalInput, onPickMedia }) {
  const [local, setLocal] = useState(slide || { image: '', title: '', link: '' });
  useEffect(() => { setLocal(slide || { image: '', title: '', link: '' }); }, [slide]);
  const commitField = (p) => { const n = { ...local, ...p }; setLocal(n); onCommit && onCommit(n); };
  return (
    <div className="admin-card" style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'grid', gap: 12 }}>
        <LocalInput label="Desktop Image URL" value={local.image} onCommit={(v) => commitField({ image: v })} />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label className="admin-btn admin-btn-secondary admin-btn-sm"> Upload <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) onUpload && onUpload(e.target.files[0]); }} /></label>
          {onPickMedia && (
            <button type="button" onClick={() => onPickMedia((url) => commitField({ image: url }))} title="Pick from Media Library" style={{ flexShrink: 0, padding: '0 10px', height: 32, borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#334155', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
              Media
            </button>
          )}
          {local.image ? <img src={resolveUploadUrl(local.image)} alt="preview" style={{ height: 48, borderRadius: 4 }} /> : null}
        </div>

        <LocalInput label="Mobile Image URL (Optional)" value={local.imageMobile} onCommit={(v) => commitField({ imageMobile: v })} />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label className="admin-btn admin-btn-secondary admin-btn-sm"> Upload Mobile <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) { if (onUploadMobile) onUploadMobile(e.target.files[0]); else if (onUpload) onUpload(e.target.files[0]); } }} /></label>
          {onPickMedia && (
            <button type="button" onClick={() => onPickMedia((url) => commitField({ imageMobile: url }))} title="Pick from Media Library" style={{ flexShrink: 0, padding: '0 10px', height: 32, borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#334155', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
              Media
            </button>
          )}
          {local.imageMobile ? <img src={resolveUploadUrl(local.imageMobile)} alt="preview" style={{ height: 48, borderRadius: 4 }} /> : null}
        </div>

        <LocalInput label="Title" value={local.title} onCommit={(v) => commitField({ title: v })} />
        <LocalInput label="Link" value={local.link} onCommit={(v) => commitField({ link: v })} />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={onRemove}>{removeLabel || 'Remove Slide'}</button>
        </div>
      </div>
    </div>
  );
}

export function SortableSlide({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    zIndex: isDragging ? 20 : 'auto',
    opacity: isDragging ? 0.8 : 1,
    position: 'relative'
  };
  if (typeof children === 'function') {
    return <div ref={setNodeRef} style={style}>{children({ attributes, listeners, isDragging })}</div>;
  }
  return <div ref={setNodeRef} style={style}>{children}</div>;
}
